import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, X } from 'lucide-react';
import { getNotifications, markNotificationRead, markAllNotificationsRead, acceptInvite, declineInvite } from '../api';
import { useOrg } from '../contexts/OrgContext';
import { setActiveOrg } from '../org';

// An "invited" notification carries its token in `link` (`/accept-invite?token=XXX`) —
// the notification document itself has no dedicated token field.
function inviteToken(n) {
  if (n.type !== 'invited' || !n.link) return null;
  try { return new URLSearchParams(n.link.split('?')[1] || '').get('token'); }
  catch { return null; }
}

// Top-bar notifications bell: unread badge + dropdown of recent notifications.
// Polls every 60s. Clicking an item marks it read and follows its link, except
// invites — those get inline Accept/Decline buttons so the user doesn't have
// to leave the dropdown to act on them.
export default function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  // notif_id -> 'pending' | error string. Accepted/declined items are removed
  // from the list rather than tracked here.
  const [inviteState, setInviteState] = useState({});
  const ref = useRef(null);
  const navigate = useNavigate();
  const { refresh: refreshOrgs } = useOrg();

  const load = useCallback(async () => {
    try { const r = await getNotifications(); setItems(r.data.notifications || []); setUnread(r.data.unread || 0); }
    catch { /* */ }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const openItem = async (n) => {
    if (!n.read) { try { await markNotificationRead(n.notif_id); } catch { /* */ } }
    setOpen(false); await load();
    if (n.link) navigate(n.link);
  };
  const readAll = async () => { try { await markAllNotificationsRead(); } catch { /* */ } await load(); };

  // Marks read, decrements the unread count, and drops the notification from
  // the list — the end state for both a successful accept/decline and a stale
  // invite (already used/expired/revoked) that has nothing left to act on.
  const dismiss = async (n) => {
    if (!n.read) { try { await markNotificationRead(n.notif_id); } catch { /* */ } setUnread((u) => Math.max(0, u - 1)); }
    setItems((its) => its.filter((it) => it.notif_id !== n.notif_id));
    setInviteState((s) => { const next = { ...s }; delete next[n.notif_id]; return next; });
  };

  const respondToInvite = async (n, accept) => {
    const token = inviteToken(n);
    if (!token || inviteState[n.notif_id] === 'pending') return;
    setInviteState((s) => ({ ...s, [n.notif_id]: 'pending' }));
    try {
      if (accept) {
        const r = await acceptInvite(token);
        if (r.data?.org_id) { setActiveOrg(r.data.org_id); await refreshOrgs(); }
      } else {
        await declineInvite(token);
      }
      await dismiss(n);
    } catch (e) {
      const status = e.response?.status;
      if (status === 404 || status === 410) {
        // Expired, already used, or revoked — nothing left to act on.
        await dismiss(n);
      } else {
        setInviteState((s) => ({ ...s, [n.notif_id]: e.response?.data?.detail || 'This invite is no longer valid' }));
      }
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => { setOpen((v) => !v); if (!open) load(); }} title="Notifications"
        className="relative p-2 rounded-lg text-fg-muted hover:text-fg hover:bg-fg/5 transition-colors">
        <Bell className="w-5 h-5" />
        {unread > 0 && <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 flex items-center justify-center text-[10px] font-bold text-white bg-orange rounded-full">{unread > 9 ? '9+' : unread}</span>}
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-80 z-50 bg-panel border border-panel-border rounded-lg shadow-lg">
          <div className="flex items-center justify-between px-3 py-2 border-b border-panel-border">
            <span className="text-sm font-semibold text-fg">Notifications</span>
            {unread > 0 && <button onClick={readAll} className="flex items-center gap-1 text-xs text-fg-muted hover:text-fg"><Check className="w-3.5 h-3.5" />Mark all read</button>}
          </div>
          <div className="max-h-96 overflow-auto">
            {items.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-fg-muted">No notifications yet.</p>
            ) : items.map((n) => {
              const token = inviteToken(n);
              const state = inviteState[n.notif_id];
              if (token) {
                return (
                  <div key={n.notif_id}
                    className={`px-3 py-2.5 border-b border-panel-border/50 ${n.read ? '' : 'bg-orange/5'}`}>
                    <div className="flex items-start gap-2">
                      {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-orange shrink-0" />}
                      <div className={n.read ? 'pl-4' : ''}>
                        <div className="text-sm text-fg font-medium">{n.title}</div>
                        <div className="text-xs text-fg-muted">{n.body}</div>
                        {state && state !== 'pending' && (
                          <div className="mt-1.5 text-xs text-red-400">{state}</div>
                        )}
                        <div className="mt-2 flex items-center justify-end gap-2">
                          <button onClick={() => respondToInvite(n, false)} disabled={state === 'pending'}
                            className="flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium text-red-400 border border-red-400/40 hover:bg-red-400/10 disabled:opacity-50">
                            <X className="w-3 h-3" />Decline
                          </button>
                          <button onClick={() => respondToInvite(n, true)} disabled={state === 'pending'}
                            className="flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium bg-orange hover:bg-orange-hover text-white disabled:opacity-50">
                            <Check className="w-3 h-3" />Accept
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }
              return (
                <button key={n.notif_id} onClick={() => openItem(n)}
                  className={`w-full text-left px-3 py-2.5 border-b border-panel-border/50 hover:bg-fg/5 ${n.read ? '' : 'bg-orange/5'}`}>
                  <div className="flex items-start gap-2">
                    {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-orange shrink-0" />}
                    <div className={n.read ? 'pl-4' : ''}>
                      <div className="text-sm text-fg font-medium">{n.title}</div>
                      <div className="text-xs text-fg-muted">{n.body}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
